<script setup lang="ts">
  import { useSlots } from 'vue';

  /**
   * White settings card on the canvas: a header with the title, a one-line
   * description and an optional action, then the body. With `flush` the body
   * has no padding so `PosUiSettingRow` rows and list rows can draw their own
   * hairlines edge to edge.
   */
  defineProps<{
    title: string;
    description?: string;
    flush?: boolean;
  }>();
  const slots = useSlots();
</script>

<template>
  <section class="pos-ui-settings-card" :class="{ flush }">
    <header class="card-header">
      <div class="card-heading">
        <div class="card-title-row">
          <h2 class="card-title">{{ title }}</h2>
          <slot name="badge" />
        </div>
        <p v-if="description" class="card-description">{{ description }}</p>
      </div>
      <div v-if="slots.actions" class="card-actions">
        <slot name="actions" />
      </div>
    </header>
    <div class="card-body"><slot /></div>
    <footer v-if="slots.footer" class="card-footer">
      <slot name="footer" />
    </footer>
  </section>
</template>

<style scoped lang="scss">
  .pos-ui-settings-card {
    @apply tw-min-w-0 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface;

    // Wrapping rows space their items with margins, not flex gap (old POS
    // hardware, Chrome < 84): each item gets half the gap on every side and
    // the row's padding (or a negative margin) absorbs the outer halves.
    .card-header {
      // Items: 8px x 6px each → 16px x 12px between them; 12px + 8px = 20px
      // (md: 16px + 8px = 24px) and 10px + 6px = 16px to the edges.
      @apply tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-px-3 tw-py-2.5 md:tw-px-4;
    }
    .card-heading {
      @apply tw-mx-2 tw-my-1.5 tw-min-w-[12rem] tw-flex-1;
    }
    .card-title-row {
      @apply tw--mx-1.5 tw--my-0.5 tw-flex tw-flex-wrap tw-items-center;

      // The title and the slotted badges: 12px x 4px between them.
      ::v-deep > * {
        @apply tw-mx-1.5 tw-my-0.5;
      }
    }
    .card-title {
      @apply tw-text-base tw-font-semibold tw-leading-snug tw-text-neutral-900;
    }
    .card-description {
      @apply tw-mb-0 tw-mt-1 tw-max-w-2xl tw-text-sm tw-leading-relaxed tw-text-neutral-500;
    }
    .card-actions {
      // The header margin (8px x 6px) minus the actions' own 4px halves.
      @apply tw-mx-1 tw-my-0.5 tw-flex tw-shrink-0 tw-flex-wrap tw-items-center;

      ::v-deep > * {
        @apply tw-m-1;
      }
    }
    .card-body {
      @apply tw-px-5 tw-py-5 md:tw-px-6;
    }
    &.flush .card-body {
      @apply tw-p-0;
    }
    .card-footer {
      @apply tw-rounded-b-xl tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-bg-canvas tw-px-5 tw-py-4 tw-text-sm tw-text-neutral-600 md:tw-px-6;
    }
  }
</style>
